import React, { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef } from "react";
import { DOCK_LAYOUT, DOCK_LAYOUT_SPECS, getDefaultSidebarOpen } from "../utils/dockLayout";

// UI preferences for the Edit experience
// - groupViewMode: 'tabs' | 'folders' | 'accordion' (default: 'tabs') - persisted to localStorage
// - selectedTabByType: remembers last selected tab path per selected element type (session only)
// - editPanelView: 'elements' | 'animations' (default: 'elements') - session only
// - docking layout preferences (session only)

const EditUIContext = createContext(null);

const isDev = process.env.NODE_ENV === "development";

// iPad / tablet breakpoint — below this width, only one panel can be open at a time.
// Value is 1023 so the JS check (<=) aligns with Tailwind's `lg:` (min-width: 1024px).
const TABLET_MAX_WIDTH = 1023;

// Helper: Get default sidebar widths based on screen size
const getDefaultSidebarWidths = () => {
  const screenWidth = window.innerWidth;
  // Tablet / iPad (<= TABLET_MAX_WIDTH): 50% for whichever panel is open
  if (screenWidth <= TABLET_MAX_WIDTH) {
    const half = Math.round(screenWidth * 0.5);
    return { left: half, right: half };
  }
  // Desktop screens (>= 1920px): 600px left, 350px right
  // Laptop screens (< 1920px): 400px left, 250px right
  if (screenWidth >= 1920) {
    return { left: 600, right: 350 };
  }
  return { left: 400, right: 250 };
};

export const useEditUI = () => {
  const ctx = useContext(EditUIContext);
  if (!ctx) throw new Error("useEditUI must be used within EditUIProvider");
  return ctx;
};

export const EditUIProvider = ({ children }) => {
  // Initialize groupViewMode from localStorage or default to "tabs"
  const [groupViewMode, setGroupViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("editUIPreferences.groupViewMode");
      if (isDev) console.log('💾 EditUIPreferences - Loading from localStorage:', saved);
      if (saved === "tabs" || saved === "folders" || saved === "accordion") {
        if (isDev) console.log('✅ EditUIPreferences - Using saved mode:', saved);
        return saved;
      }
    } catch (e) {
      console.error('❌ EditUIPreferences - localStorage error:', e);
    }
    if (isDev) console.log('⚙️ EditUIPreferences - Using default mode: tabs');
    return "tabs";
  });
  // Map of elementType -> { path: string[] }
  const [selectedTabByType, setSelectedTabByType] = useState({});

  // Right-dock panel view (session-only)
  const [editPanelView, setEditPanelView] = useState("elements");

  // Docking layout state (session-only)
  const [dockLayout, setDockLayout] = useState(DOCK_LAYOUT.SPLIT_RL);
  const [leftSidebarOpen, setLeftSidebarOpenBase] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpenBase] = useState(() => {
    // On tablet / iPad, right sidebar starts closed — the left (edit) panel is the default.
    if (typeof window !== 'undefined' && window.innerWidth <= TABLET_MAX_WIDTH) return false;
    return true;
  });

  // ── Tablet / iPad single-panel enforcement ──────────────────────────────────
  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= TABLET_MAX_WIDTH;
  });
  const isTabletRef = useRef(isTablet);
  useEffect(() => { isTabletRef.current = isTablet; }, [isTablet]);

  // Wrapped setters: on iPad-sized screens, opening one sidebar closes the other
  // and resets the opening sidebar to 50 % of the viewport width.
  const setLeftSidebarOpen = useCallback((open) => {
    setLeftSidebarOpenBase(open);
    if (open && isTabletRef.current) {
      setRightSidebarOpenBase(false);
      setLeftSidebarWidthPx(Math.round(window.innerWidth * 0.5));
    }
  }, []);

  const setRightSidebarOpen = useCallback((open) => {
    setRightSidebarOpenBase(open);
    if (open && isTabletRef.current) {
      setLeftSidebarOpenBase(false);
      setRightSidebarWidthPx(Math.round(window.innerWidth * 0.5));
    }
  }, []);

  // Safety net: when the viewport transitions *into* tablet size while both
  // sidebars happen to be open, close the right one (left/edit is the default).
  useEffect(() => {
    if (isTablet && leftSidebarOpen && rightSidebarOpen) {
      setRightSidebarOpenBase(false);
    }
  }, [isTablet, leftSidebarOpen, rightSidebarOpen]);

  // Initialize sidebar widths based on screen size
  const [leftSidebarWidthPx, setLeftSidebarWidthPx] = useState(() => getDefaultSidebarWidths().left);
  const [rightSidebarWidthPx, setRightSidebarWidthPx] = useState(() => getDefaultSidebarWidths().right);

  // Helper: whether a side is active for a given layout
  const isSideActive = useCallback((layout, side) => {
    const spec = DOCK_LAYOUT_SPECS[layout] || {};
    const sides = spec.sides || {};
    return Boolean(sides[side]);
  }, []);

  // Save groupViewMode to localStorage whenever it changes
  useEffect(() => {
    if (isDev) console.log('💾 EditUIPreferences - Saving mode to localStorage:', groupViewMode);
    try {
      localStorage.setItem("editUIPreferences.groupViewMode", groupViewMode);
      if (isDev) console.log('✅ EditUIPreferences - Saved successfully');
    } catch (e) {
      console.error('❌ EditUIPreferences - Save error:', e);
    }
  }, [groupViewMode]);

  // Preserve open/close for still-active sides; initialize only newly active sides
  const prevLayoutRef = useRef(dockLayout);
  useEffect(() => {
    const prevLayout = prevLayoutRef.current;
    const nextLayout = dockLayout;
    if (prevLayout === nextLayout) return;

    const prevLeftActive = isSideActive(prevLayout, 'left');
    const prevRightActive = isSideActive(prevLayout, 'right');
    const nextLeftActive = isSideActive(nextLayout, 'left');
    const nextRightActive = isSideActive(nextLayout, 'right');

    const defaults = getDefaultSidebarOpen(nextLayout);

    // Left side (use base setters — the tablet constraint effect enforces single-panel)
    if (nextLeftActive && prevLeftActive) {
      // preserve as-is
    } else if (nextLeftActive && !prevLeftActive) {
      setLeftSidebarOpenBase(defaults.left);
    } else if (!nextLeftActive) {
      setLeftSidebarOpenBase(false);
    }

    // Right side (base setters — same reasoning as above)
    if (nextRightActive && prevRightActive) {
      // preserve as-is
    } else if (nextRightActive && !prevRightActive) {
      setRightSidebarOpenBase(defaults.right);
    } else if (!nextRightActive) {
      setRightSidebarOpenBase(false);
    }

    prevLayoutRef.current = nextLayout;
  }, [dockLayout, isSideActive]);

  // Update tablet breakpoint + sidebar widths when screen size changes
  useEffect(() => {
    const handleResize = () => {
      const nowTablet = window.innerWidth <= TABLET_MAX_WIDTH;
      // Track tablet breakpoint for single-panel enforcement
      setIsTablet(nowTablet);

      const currentDefaults = getDefaultSidebarWidths();

      if (nowTablet) {
        // On tablet, always keep the open sidebar at 50 % of the viewport
        const half = Math.round(window.innerWidth * 0.5);
        setLeftSidebarWidthPx(half);
        setRightSidebarWidthPx(half);
      } else {
        // Only update widths if they're still at a default value (preserves user customisation)
        setLeftSidebarWidthPx((prevWidth) => {
          if (prevWidth === 600 || prevWidth === 400 || prevWidth === Math.round(window.innerWidth * 0.5)) {
            return currentDefaults.left;
          }
          return prevWidth;
        });

        setRightSidebarWidthPx((prevWidth) => {
          if (prevWidth === 350 || prevWidth === 250 || prevWidth === Math.round(window.innerWidth * 0.5)) {
            return currentDefaults.right;
          }
          return prevWidth;
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setSelectedTabPathForType = useCallback((elementType, pathArray) => {
    const key = elementType || "_unknown_";
    const nextPath = Array.isArray(pathArray) ? pathArray : [];
    setSelectedTabByType((prev) => {
      const prevPath = prev?.[key]?.path || [];
      // Avoid redundant state updates (prevents render thrash when callers set the same path repeatedly)
      if (
        Array.isArray(prevPath) &&
        prevPath.length === nextPath.length &&
        prevPath.every((v, idx) => v === nextPath[idx])
      ) {
        return prev;
      }
      return {
        ...prev,
        [key]: { path: nextPath },
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      groupViewMode,
      setGroupViewMode,
      selectedTabByType,
      setSelectedTabPathForType,
      editPanelView,
      setEditPanelView,
      dockLayout,
      setDockLayout,
      leftSidebarOpen,
      setLeftSidebarOpen,
      rightSidebarOpen,
      setRightSidebarOpen,
      leftSidebarWidthPx,
      setLeftSidebarWidthPx,
      rightSidebarWidthPx,
      setRightSidebarWidthPx,
      isTablet,
    }),
    [
      groupViewMode,
      selectedTabByType,
      setSelectedTabPathForType,
      editPanelView,
      dockLayout,
      leftSidebarOpen,
      rightSidebarOpen,
      leftSidebarWidthPx,
      rightSidebarWidthPx,
      isTablet,
    ]
  );

  return <EditUIContext.Provider value={value}>{children}</EditUIContext.Provider>;
};


